import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Circle, CheckCircle2, AlertTriangle, Hand, Unlink, ChevronDown, ClipboardList, Send, ImageDown, Plus, Landmark, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { PersonBadge, PersonDot } from "@/components/PersonBadge";
import { ReceiptCard } from "@/components/ReceiptCard";
import { getShare, sharePay, shareClaim } from "@/lib/api";
import { idr, money, buildBreakdownText, whatsappUrl, personBreakdown, computeInitialsMap, itemUnits, portionFor, fmtQty, exportNodeAsImage, warmImageExport } from "@/lib/format";
import { describePayment } from "@/lib/payments";

export default function SharePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [me, setMe] = useState(null); // participant id for claiming
  const [open, setOpen] = useState({});
  const [saving, setSaving] = useState(false);
  const [copiedAcc, setCopiedAcc] = useState(false);
  const receiptRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        setData(await getShare(token));
      } catch (e) {
        setError(e?.response?.status === 410 ? "This share link has expired." : "Share link not found.");
      }
    })();
  }, [token]);

  useEffect(() => { warmImageExport(); }, []);

  const togglePaid = async (pid, paid) => {
    try {
      const res = await sharePay(token, pid, paid);
      setData((d) => ({ ...d, summary: res.summary }));
    } catch {
      toast.error("Could not update payment.");
    }
  };

  const claim = async (itemId, claimVal) => {
    if (!me) return;
    try {
      const res = await shareClaim(token, me, itemId, claimVal);
      setData((d) => ({ ...d, bill: res.bill, summary: res.summary }));
    } catch {
      toast.error("Could not update item.");
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-[var(--gz-body)]">
        <div className="max-w-md mx-auto">
          <Header title="gigazip splitbill" />
        </div>
        <div className="max-w-md mx-auto px-5 -mt-16 relative">
          <div className="rounded-2xl bg-[var(--gz-card)] gz-paper-shadow p-8 text-center">
            <Unlink size={26} className="mx-auto text-[var(--gz-muted)] mb-2" />
            <p className="font-medium">{error}</p>
            <p className="text-sm text-[var(--gz-muted)] mt-1">This split is no longer available. Start a fresh one below.</p>
            <button
              data-testid="expired-start-new-btn"
              onClick={() => navigate("/")}
              className="mt-5 w-full h-12 rounded-[12px] bg-[var(--gz-primary)] text-white text-[13px] font-semibold flex items-center justify-center gap-2 active:scale-[0.99] transition"
            >
              <Plus size={17} strokeWidth={1.9} /> Start a new bill
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-screen grid place-items-center bg-[var(--gz-body)] text-[var(--gz-muted)]">Loading…</div>;

  const { bill, summary } = data;
  const cur = summary.currency || "IDR";
  const dec = summary.decimals ?? 0;
  const m = (v) => money(v, cur);
  const people = summary.people || [];
  const imap = computeInitialsMap(people);
  const pd = describePayment(bill.payment);

  const breakdownText = () =>
    buildBreakdownText({
      title: bill.title,
      participants: bill.participants || [],
      items: bill.items || [],
      summary,
      url: window.location.href,
      payment: bill.payment,
    });
  const copyBreakdown = async () => {
    const text = breakdownText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Breakdown copied, paste it anywhere");
    } catch {
      toast.message("Copy this breakdown", { description: text });
    }
  };
  const waHref = whatsappUrl(breakdownText());
  const saveImage = async () => {
    if (!receiptRef.current) return;
    setSaving(true);
    try {
      const result = await exportNodeAsImage(
        receiptRef.current,
        `gigazip-${(bill.title || "bill").replace(/\s+/g, "-").toLowerCase()}.png`
      );
      if (result === "shared") toast.success("Receipt image ready to share");
      else toast.success("Receipt image saved");
    } catch (e) {
      toast.error("Could not create the image.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[var(--gz-body)] pb-16">
      <div className="max-w-md mx-auto">
        <Header title={bill.title || "Shared bill"} subtitle="Read-only breakdown" />
      </div>

      <div className="max-w-md mx-auto px-5 -mt-16 relative space-y-4">
        <div className="rounded-2xl bg-[var(--gz-card)] gz-paper-shadow p-5">
          <div className="flex items-center justify-between py-1">
            <span className="text-sm text-[var(--gz-muted)]">Subtotal</span>
            <span className="text-sm tabular-nums">{m(summary.subtotal)}</span>
          </div>
          {summary.discount > 0 && (
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-[var(--gz-muted)]">Discount</span>
              <span className="text-sm tabular-nums">− {m(summary.discount)}</span>
            </div>
          )}
          {summary.service_amount > 0 && (
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-[var(--gz-muted)]">Service{summary.service_from_pct && summary.service_pct ? ` (${summary.service_pct}%)` : ""}</span>
              <span className="text-sm tabular-nums">{m(summary.service_amount)}</span>
            </div>
          )}
          {summary.tax_amount > 0 && (
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-[var(--gz-muted)]">Tax{summary.tax_from_pct && summary.tax_pct ? ` (${summary.tax_pct}%)` : ""}</span>
              <span className="text-sm tabular-nums">{m(summary.tax_amount)}</span>
            </div>
          )}
          {summary.rounding !== 0 && (
            <div className="flex items-center justify-between py-1">
              <span className="text-sm text-[var(--gz-muted)]">Rounding</span>
              <span className="text-sm tabular-nums">{summary.rounding < 0 ? "− " : ""}{m(Math.abs(summary.rounding))}</span>
            </div>
          )}
          <div className="border-t border-[var(--gz-line)] mt-2 pt-2 flex items-center justify-between">
            <span className="font-semibold">Grand total</span>
            <span className="text-lg font-semibold tabular-nums">{m(summary.grand_total)}</span>
          </div>
        </div>

        {summary.has_unassigned && (
          <div className="flex items-center gap-2 rounded-xl bg-amber-500/10 text-amber-600 dark:text-amber-400 px-4 py-3 text-sm">
            <AlertTriangle size={18} /> {m(summary.unassigned_amount)} of items are still unassigned.
          </div>
        )}

        {pd && (
          <div data-testid="share-payment-card" className="rounded-2xl bg-[var(--gz-primary)]/[0.06] border border-[var(--gz-primary)]/25 p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Landmark size={18} className="text-[var(--gz-primary)]" />
                <h3 className="font-semibold">Everyone pays to</h3>
              </div>
              <span className="text-xs font-semibold text-[var(--gz-primary)] bg-[var(--gz-primary)]/10 rounded-full px-2.5 py-1">{pd.currency}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <div className="text-sm text-[var(--gz-muted)]">{pd.headline}</div>
                {pd.rows.filter((r) => r.value !== pd.copyValue).map((r) => (
                  <div key={r.key} className="text-sm text-[var(--gz-muted)]">{r.label}: <span className="text-[var(--gz-ink)] font-medium">{r.value}</span></div>
                ))}
                <div className="text-xl font-semibold tracking-wide tabular-nums break-all">{pd.copyValue}</div>
                {pd.name && <div className="text-sm text-[var(--gz-muted)] mt-0.5">a/n {pd.name}</div>}
              </div>
              {pd.copyValue && (
                <button
                  data-testid="copy-account-btn"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(pd.copyValue);
                      setCopiedAcc(true);
                      toast.success("Copied");
                      setTimeout(() => setCopiedAcc(false), 1800);
                    } catch {
                      toast.message("Copy this", { description: pd.copyValue });
                    }
                  }}
                  aria-label="Copy"
                  className="shrink-0 grid place-items-center h-11 w-11 rounded-full bg-[var(--gz-primary)] text-white active:scale-90 transition-transform"
                >
                  {copiedAcc ? <Check size={18} /> : <Copy size={18} />}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Claim your items */}
        <div className="rounded-2xl bg-[var(--gz-card)] gz-paper-shadow p-5">
          <div className="flex items-center gap-2 mb-3">
            <Hand size={18} className="text-[var(--gz-primary)]" />
            <h3 className="font-semibold">Claim your items</h3>
          </div>
          <p className="text-xs text-[var(--gz-muted)] mb-3">Pick who you are, then tap the dishes you had.</p>
          <div className="flex items-center gap-2 flex-wrap mb-4">
            {people.map((p) => (
              <button
                key={p.id}
                data-testid={`claim-as-${p.id}`}
                onClick={() => setMe(me === p.id ? null : p.id)}
                className={`flex items-center gap-2 h-10 pl-1 pr-3 rounded-full border transition-colors ${
                  me === p.id ? "border-[var(--gz-primary)] bg-[var(--gz-primary)]/10" : "border-[var(--gz-line)]"
                }`}
              >
                <PersonBadge person={p} size={30} label={imap[p.id]} />
                <span className="text-sm font-medium">{p.name}</span>
              </button>
            ))}
          </div>

          {me && (
            <div className="space-y-2 gz-rise">
              {bill.items.map((it) => {
                const units = itemUnits(it);
                const mineUnits = portionFor(it, me);
                const mine = mineUnits > 0;
                return (
                  <button
                    key={it.id}
                    data-testid={`claim-item-${it.id}`}
                    onClick={() => claim(it.id, !mine)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                      mine ? "border-[var(--gz-primary)] bg-[var(--gz-primary)]/10" : "border-[var(--gz-line)]"
                    }`}
                  >
                    {mine ? <CheckCircle2 size={20} className="text-[var(--gz-primary)]" /> : <Circle size={20} className="text-[var(--gz-muted)]" />}
                    <span className="flex-1 min-w-0">
                      <span className="block font-medium truncate">{it.name || "Item"}</span>
                      <span className="text-xs text-[var(--gz-muted)]">
                        {units} × {m(it.unit_price)}
                        {mineUnits > 1 && ` · you: ${fmtQty(mineUnits)}`}
                      </span>
                    </span>
                    <span className="text-sm font-semibold tabular-nums">{m(units * it.unit_price)}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Per person + pay */}
        <div className="rounded-2xl bg-[var(--gz-card)] gz-paper-shadow p-5">
          <h3 className="font-semibold mb-3">Per person</h3>
          <div className="space-y-2">
            {people.map((p) => {
              const isOpen = open[p.id];
              return (
                <div key={p.id} data-testid={`share-person-${p.id}`} className={`rounded-xl border transition-colors ${p.paid ? "border-emerald-500/40 bg-emerald-500/5" : "border-[var(--gz-line)]"}`}>
                  <div className="flex items-center gap-3 p-3">
                    <PersonBadge person={p} size={38} label={imap[p.id]} />
                    <button className="flex-1 min-w-0 text-left" onClick={() => setOpen((o) => ({ ...o, [p.id]: !o[p.id] }))} data-testid={`share-breakdown-${p.id}`}>
                      <div className="flex items-center gap-1 font-medium truncate">
                        {p.name}
                        <ChevronDown size={15} className={`text-[var(--gz-muted)] transition-transform ${isOpen ? "rotate-180" : ""}`} />
                      </div>
                      <div className="text-xs text-[var(--gz-muted)]">{p.paid ? "Paid" : "Owes"}</div>
                    </button>
                    <div className="font-semibold tabular-nums shrink-0 whitespace-nowrap">{m(p.total)}</div>
                    <button
                      data-testid={`share-pay-${p.id}`}
                      onClick={() => togglePaid(p.id, !p.paid)}
                      aria-label={p.paid ? "Mark unpaid" : "Mark paid"}
                      className="grid place-items-center h-10 w-10 rounded-full active:scale-90 transition-transform"
                    >
                      {p.paid ? <CheckCircle2 size={24} className="text-emerald-500" /> : <Circle size={24} className="text-[var(--gz-muted)]" />}
                    </button>
                  </div>
                  {isOpen && (() => {
                    const bd = personBreakdown(p, bill.items || [], dec);
                    return (
                      <div className="px-4 pb-3 pt-1 border-t border-[var(--gz-line)] text-sm gz-rise">
                        {bd.lines.map((l) => (
                          <div key={l.id} className="flex items-center justify-between py-1">
                            <span className="text-[var(--gz-muted)] truncate pr-2">{l.name}</span>
                            <span className="tabular-nums">{m(l.amount)}</span>
                          </div>
                        ))}
                        <div className="border-t border-[var(--gz-line)] mt-1 pt-1">
                          <div className="flex items-center justify-between py-1"><span className="text-[var(--gz-muted)]">Items subtotal</span><span className="tabular-nums">{m(bd.itemsSubtotal)}</span></div>
                          {bd.discount > 0 && <div className="flex items-center justify-between py-1"><span className="text-[var(--gz-muted)]">Discount</span><span className="tabular-nums">− {m(bd.discount)}</span></div>}
                          {bd.service > 0 && <div className="flex items-center justify-between py-1"><span className="text-[var(--gz-muted)]">Service</span><span className="tabular-nums">{m(bd.service)}</span></div>}
                          {bd.tax > 0 && <div className="flex items-center justify-between py-1"><span className="text-[var(--gz-muted)]">Tax</span><span className="tabular-nums">{m(bd.tax)}</span></div>}
                          {bd.rounding !== 0 && <div className="flex items-center justify-between py-1"><span className="text-[var(--gz-muted)]">Rounding</span><span className="tabular-nums">{bd.rounding < 0 ? "− " : "+ "}{m(Math.abs(bd.rounding))}</span></div>}
                        </div>
                      </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>

          <div className="grid grid-cols-2 gap-2 mt-4">
            <button
              data-testid="copy-text-btn"
              onClick={copyBreakdown}
              className="h-12 rounded-full border border-[var(--gz-line)] font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
            >
              <ClipboardList size={17} /> Copy as text
            </button>
            <a
              data-testid="whatsapp-btn"
              href={waHref}
              target="_blank"
              rel="noopener noreferrer"
              className="h-12 rounded-full bg-[#25D366] text-white font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform"
            >
              <Send size={16} /> WhatsApp
            </a>
          </div>

          <button
            data-testid="save-image-btn"
            onClick={saveImage}
            disabled={saving}
            className="mt-2 w-full h-12 rounded-full border border-[var(--gz-line)] font-medium flex items-center justify-center gap-2 active:scale-95 transition-transform disabled:opacity-60"
          >
            <ImageDown size={17} /> {saving ? "Saving…" : "Save as image"}
          </button>
        </div>

        {/* Off-screen capture target for "Save as image" */}
        <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
          <ReceiptCard ref={receiptRef} title={bill.title} summary={summary} participants={bill.participants || []} items={bill.items || []} payment={bill.payment} />
        </div>

        <p className="text-center text-xs text-[var(--gz-muted)]">
          Powered by gigazip · totals are computed on the server
        </p>
      </div>
    </div>
  );
}
